import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import isPostalCode from 'validator/lib/isPostalCode';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { func, shape, string } from 'prop-types';
import validator from 'validator';

const { isPostalCodeLocales } = validator;

export default function AddressInputs({ setAddressInputs = () => {}, initialValues }) {
    const [address1, setAddress1] = useState(initialValues.address1 ?? '');
    const [address2, setAddress2] = useState(initialValues.address2 ?? '');
    const [city, setCity] = useState(initialValues.city ?? '');
    const [zip, setZip] = useState(initialValues.zip ?? '');
    const [stateCode, setStateCode] = useState(initialValues.stateCode ?? '');
    const [countryCode, setCountryCode] = useState(initialValues.countryCode ?? '');
    const [firstName, setFirstName] = useState(initialValues.firstName ?? '');
    const [lastName, setLastName] = useState(initialValues.lastName ?? '');
    const [company, setCompany] = useState(initialValues.company ?? '');
    const [phone, setPhone] = useState(initialValues.phone ?? '');
    const [countryData, setCountryData] = useState([]);
    const [stateData, setStateData] = useState([]);
    const [makeDefault, setMakeDefault] = useState(initialValues.isDefault ?? false);
    const fetchCountryData = useCallback(async () => {
        try {
            const response = await fetch(process.env.APP_URL + 'api/countries/', {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            const data = await response.json();
            if (data.countries) {
                setCountryData(data.countries);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);
    const fetchStateData = useCallback(async () => {
        try {
            const response = await fetch(process.env.APP_URL + 'api/states?page=0&countryCode=' + countryCode, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            const data = await response.json();
            if (data.states) {
                setStateData(data.states);
            }
        } catch (e) {
            console.error(e);
        }
    }, [countryCode]);
    useEffect(() => {
        fetchCountryData();
    }, [fetchCountryData]);
    useEffect(() => {
        if (countryCode !== '') {
            fetchStateData();
        }
    }, [fetchStateData, countryCode]);
    useEffect(() => {
        setAddressInputs({
            address1,
            address2,
            city,
            zip,
            phone,
            company,
            firstName,
            lastName,
            makeDefault,
            State: {
                stateCode,
            },
            Country: {
                countryCode,
            },
        });
    }, [address1, address2, city, zip, phone, company, firstName, lastName, stateCode, countryCode, makeDefault]);
    const locale = isPostalCodeLocales.filter(x => x === countryCode).length > 0 ? countryCode : 'any';
    return (<>
        <Form.Group
            className={'mb-3'}>
            <Form.Label>Country: <span
                style={{ color: 'red',
                    fontSize: '0.7rem' }}>(required)</span></Form.Label>
            <Form.Select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}>
                <option
                    value={''}>Select Country</option>
                <option
                    value={''}
                    disabled>---</option>
                {countryData.map((country) => (<option
                    key={country.countryCode}
                    value={country.countryCode}>{country.title}</option>))}
            </Form.Select>
        </Form.Group>
        {countryCode !== '' && (<>
            <Form.Group
                className={'mb-3'}>
                <Form.Label>State: <span
                    style={{ color: 'red',
                        fontSize: '0.7rem' }}>(required)</span></Form.Label>
                <Form.Select
                    value={stateCode}
                    onChange={(e) => setStateCode(e.target.value)}>
                    <option
                        value={''}>Select State</option>
                    <option
                        value={''}
                        disabled>---</option>
                    {stateData.map((state) => (<option
                        key={state.stateCode}
                        value={state.stateCode}>{state.title}</option>))}
                </Form.Select>
            </Form.Group>
            <Form.Group
                className={'mb-3'}>
                <Form.Label>
                    Address Line 1 <span
                        style={{ color: 'red',
                            fontSize: '0.7rem' }}>(required)</span>
                </Form.Label>
                <Form.Control
                    value={address1}
                    onChange={(e) => setAddress1(e.target.value)}
                    placeholder={'Enter Address Line 1'}
                    isValid={address1.length > 0}></Form.Control>
            </Form.Group>
            <Form.Group
                className={'mb-3'}>
                <Form.Label>
                    Address Line 2
                </Form.Label>
                <Form.Control
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    placeholder={'Enter Address Line 2'}
                    isValid={address2.length > 0}></Form.Control>
            </Form.Group>
            <Form.Group
                className={'mb-3'}>
                <Form.Label>
                    City
                </Form.Label>
                <Form.Control
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder={'Enter City'}
                    isValid={city.length > 0}></Form.Control>
            </Form.Group>
            <Form.Group
                className={'mb-3'}>
                <Form.Label>
                    Postal Code <span
                        style={{ color: 'red',
                            fontSize: '0.7rem' }}>(required)</span>
                </Form.Label>
                <Form.Control
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    placeholder={'Enter ZIP'}
                    isValid={(() => {
                        try {
                            return isPostalCode(zip, locale);
                        } catch {
                            return false;
                        }
                    })()}></Form.Control>
            </Form.Group>
            <Form.Group
                className={'mb-3'}>
                <Form.Label>
                    First Name <span
                        style={{ color: 'red',
                            fontSize: '0.7rem' }}>(required)</span>
                </Form.Label>
                <Form.Control
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={'Enter First Name'}
                    isValid={firstName.length > 0}></Form.Control>
            </Form.Group>
            <Form.Group
                className={'mb-3'}>
                <Form.Label>
                    Last Name <span
                        style={{ color: 'red',
                            fontSize: '0.7rem' }}>(required)</span>
                </Form.Label>
                <Form.Control
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={'Enter Last Name'}
                    isValid={lastName.length > 0}></Form.Control>
            </Form.Group>
            <Form.Group
                className={'mb-3'}>
                <Form.Label>
                    Company
                </Form.Label>
                <Form.Control
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder={'Enter Company Name'}
                    isValid={company.length > 0}></Form.Control>
            </Form.Group>
            <Form.Group
                className={'mb-3'}>
                <Form.Label>
                    Phone Number <span
                        style={{ color: 'red',
                            fontSize: '0.7rem' }}>(required)</span>
                </Form.Label>
                <Form.Control
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={'Enter Mobile Phone No.'}
                    isValid={isMobilePhone(phone)}></Form.Control>
            </Form.Group>
            <Form.Group
                controlId={'addressInputForm.makeDefault'}
                className={'mb-3'}>
                <Form.Check
                    checked={makeDefault}
                    onChange={(e) => setMakeDefault(e.target.checked)}
                    label={'Set as Default Address'}></Form.Check>
            </Form.Group>
        </>)}
    </>);
}

AddressInputs.propTypes = {
    setAddressInputs: func,
    initialValues: shape({
        firstName: string,
        lastName: string,
        address1: string,
        address2: string,
        city: string,
        zip: string,
        stateCode: string,
        countryCode: string,
        company: string,
        phone: string,
    })
};